import {NgClass, NgOptimizedImage, SlicePipe} from '@angular/common';
import {Component, computed, EventEmitter, inject, input, Output} from '@angular/core';
import {IonCardContent, IonCol, IonGrid, IonIcon, IonRow, IonText} from '@ionic/angular/standalone';
import {TranslocoDirective} from '@jsverse/transloco';
import {addIcons} from 'ionicons';
import {banOutline, ellipse, mailOutline, personOutline, star, starOutline, trashOutline} from 'ionicons/icons';
import {environment} from '../../../environments/environment';
import {AppAvatarComponent} from '@components';
import {Profile} from '@models';
import {Astro2LabelMapping, Gender2LabelMapping, Pronouns2LabelMapping, Role2LabelMapping} from '@enums';
import {AnalyticsService, logger, PresenceService, PrivateMessageService, ProfileService} from '@services';

@Component({
	           selector: 'app-user-card',
	           standalone: true,
	           templateUrl: './user-card.component.html',
	           styleUrls: ['./user-card.component.scss'],
	           imports: [
			TranslocoDirective,
		           IonGrid,
		           IonCardContent,
		           IonRow,
		           IonCol,
		           NgOptimizedImage,
		           NgClass,
		           IonIcon,
		           SlicePipe,
		           IonText,
		           AppAvatarComponent
	           ]
           })
export class UserCardComponent {
	selectedProfile = input.required<Profile>();
	profile = input.required<Profile>();
	sourcePage = input('search');
	@Output() NavigateRequired: EventEmitter<string> = new EventEmitter<string>();
	public gender2LabelMapping = Gender2LabelMapping;
	public roles2LabelMapping = Role2LabelMapping;
	public astro2LabelMapping = Astro2LabelMapping;
	public pronouns2LabelMapping = Pronouns2LabelMapping;
	protected readonly environment = environment;
	protected readonly Profile = Profile;
	private presenceService = inject(PresenceService);
	private privateMessageService = inject(PrivateMessageService);
	private profileService: ProfileService = inject(ProfileService);
	private analyticsService: AnalyticsService = inject(AnalyticsService);
	protected currentProfile = computed(() => this.profileService.profile());
	protected readonly isBlocked = computed(() => {
		const current = this.profileService.profile();
		const selected = this.selectedProfile();
		if (!current || !selected) {
			return false;
		}
		return current.blocked?.includes(selected.uuid) ?? false;
	});
	protected readonly isFavorite = computed(() => {
		const current = this.profileService.profile();
		const selected = this.selectedProfile();
		if (!current || !selected) {
			return false;
		}
		return current.favorits?.includes(selected.uuid) ?? false;
	});
	protected readonly presenceStatus = computed(() => {
		const selected = this.selectedProfile();
		if (!selected?.uuid) {
			return 'offline';
		}
		const snapshot = this.presenceService.getPresence(selected.uuid)();
		return snapshot?.status ?? 'offline';
	});

	// Calculate compatibility score between current profile and selected profile
	protected compatibilityScore = computed(() => {
		const current = this.profile();
		const selected = this.selectedProfile();
		if (!current || !selected || current.uuid === selected.uuid) {
			return null;
		}
		return current.calculateCompatibilityScore(selected);
	});

	constructor() {
		addIcons({trashOutline, ellipse, banOutline, starOutline, personOutline, mailOutline, star});
	}

	sendPrivateMessage(selectedProfile: Profile) {
		if (this.currentProfile() && this.currentProfile()!.uuid !== selectedProfile.uuid) {
			this.privateMessageService.sendPrivateMessage(this.currentProfile()!, selectedProfile);
		}
	}

	blockUser() {
		this.profileService.blockUser(this.selectedProfile().uuid);
	}

	addUserFavorit() {
		this.analyticsService.trackEvent('engagement_favorite_added', {
			source_page: this.sourcePage(),
			target_profile_uid: this.selectedProfile().uuid
		});
		this.profileService.addUserFavorit(this.selectedProfile().uuid);
	}

	unblockUser() {
		this.profileService.unblockUser(this.selectedProfile().uuid);
	}

	removeUserFavorit() {
		this.profileService.removeUserFavorit(this.selectedProfile().uuid).then();
	}

	navigateToBigProfile() {
		const username = this.selectedProfile()?.username;
		this.analyticsService.trackEvent('engagement_profile_opened', {
			source_page: this.sourcePage(),
			target_profile_uid: this.selectedProfile()?.uuid ?? ''
		});
		if (!username) {
			return;
		}
		this.NavigateRequired.emit(`/app/visitor/${username}`);
	};

	deleteUser() {
		if (!this.currentProfile()) {
			return;
		}
		if (this.selectedProfile().uuid !== this.currentProfile()!.uuid) {
			try {
				this.profileService.delete(this.selectedProfile().uuid);
			} catch (e) {
				logger.error(e);
			}

		} else {
			logger.error('Tentative de suppression du compte principale');
		}

	}

	protected getInitials(username: string): string {
		if (!username || !username.trim()) {
			return '?';
		}
		const parts = username.trim().split(/\s+/);
		if (parts.length >= 2) {
			return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
		}
		return username.slice(0, 2).toUpperCase();
	}

}
